<template>
  <div class="section__lk-content container-shadow fill-white admin-dashboard">
    <div class="section__lk-title">Статистика курсов</div>

    <div class="row">
      <div class="col-md-8">
        <label class="section__lk-label" for="program"><span>Программа</span></label>
        <v-select
          id="program"
          :options="programs"
          :clearable="false"
          :searchable="false"
          label="title"
          v-model="program"
        />
      </div>
      <div class="col-md-4">
        <label class="section__lk-label" for="start-date"><span>Дата начала обучения с</span></label>
        <sweet-date-picker
          name="start-date"
          type="date"
          :range="false"
          v-model="startDate"
          placeholder="Выберите дату"
        />
      </div>
    </div>
    <template v-if="loaded">
      <seminar-stat-row :stat="topStat" />

      <div class="section__lk-content-items mt-0">
        <div class="section__lk-content-items-title">Курсы</div>

        <div v-if="stat.courses.length === 0" class="section__lk-empty">
          Нет курсов в выбранной программе
        </div>

        <div v-for="(c, idx) in stat.courses" :key="c.id" class="course-card" :class="idx % 2 === 0 ? 'fill-white' : 'fill-grey'">
        <div class="course-card__head" @click="toggle(c.id)">
          <span class="course-card__title">{{ c.title }}</span>
          <span class="course-card__chevron" :class="{ 'is-open': expanded[c.id] }">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9l6 6 6-6" stroke="#002E5E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
        <div class="course-card__body">
          <div class="module-stat course-stat">
            <div class="module-stat__cell" v-for="cell in courseCells(c)" :key="cell.label">
              <img :src="cell.icon" />
              <div>
                <div class="module-stat__value">{{ cell.value }}</div>
                <div class="module-stat__label">{{ cell.label }}</div>
              </div>
            </div>
          </div>

          <div v-if="expanded[c.id]" class="modules">
            <div v-for="m in c.modules" :key="m.id" class="module-block">
              <div class="module-block__title">Модуль {{ m.index }}. {{ m.title }}</div>
              <div class="module-stat">
                <div class="module-stat__cell" v-for="cell in moduleCells(m)" :key="cell.label">
                  <img :src="cell.icon" />
                  <div>
                    <div class="module-stat__value">{{ cell.value }}</div>
                    <div class="module-stat__label">{{ cell.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ApiService from "../../../services/api.service";
import SweetDatePicker from "@/components/form/SweetDatePicker.vue";
import SeminarStatRow from "@/components/profile/seminar/SeminarStatRow.vue";

const defaultStartDate = () => new Date(new Date().getFullYear(), 0, 1);

const formatYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default {
  name: "AdminDashboard",
  components: { SweetDatePicker, SeminarStatRow },
  data() {
    return {
      loaded: false,
      programs: [],
      program: null,
      startDate: defaultStartDate(),
      stat: { all_unique_online: 0, courses: [] },
      expanded: {}
    };
  },
  computed: {
    topStat() {
      const courses = (this.stat && Array.isArray(this.stat.courses)) ? this.stat.courses : [];
      let certificated = 0, testCompleted = 0;
      courses.forEach(c => {
        const d = (c && c.statistic && c.statistic.data) || {};
        certificated += d.online_certificated || 0;
        testCompleted += d.online_test_completed || 0;
      });
      return {
        count_participants: this.stat.all_unique_online || 0,
        participants: this.stat.approved_count || 0,
        test_solved_count: testCompleted,
        cert_count: certificated
      };
    }
  },
  methods: {
    toggle(id) { this.$set(this.expanded, id, !this.expanded[id]); },
    courseCells(c) {
      const d = (c && c.statistic && c.statistic.data) || {};
      const lineage = d.online_review_lineage || {};
      const reviewCount = Object.keys(lineage).reduce((s, k) => s + (lineage[k] || 0), 0);
      return [
        { label: 'Сданных тестов', value: d.online_test_completed || 0, icon: '/pic/stats/test_solved_count.svg' },
        { label: 'Получили сертификаты', value: d.online_certificated || 0, icon: '/pic/stats/cert_count.svg' },
        { label: 'Средняя оценка', value: (d.online_review_rate || 0).toFixed(2), icon: '/pic/stats/participants.svg' },
        { label: 'Кол-во отзывов', value: reviewCount, icon: '/pic/stats/count_participants.svg' }
      ];
    },
    moduleCells(m) {
      const d = (m && m.statistic && m.statistic.data) || {};
      return [
        { label: 'Проходят сейчас', value: d.active || 0, icon: '/pic/stats/count_participants.svg' },
        { label: 'Завершили модуль', value: d.completed || 0, icon: '/pic/stats/participants.svg' },
        { label: 'Сданных тестов', value: d.completed_test || 0, icon: '/pic/stats/test_solved_count.svg' },
        { label: 'Просмотров видео', value: d.video_watched || 0, icon: '/pic/stats/count_invites.svg' }
      ];
    },
    async loadStat() {
      if (!this.program) return;
      const date = this.startDate instanceof Date
        ? formatYmd(this.startDate)
        : (typeof this.startDate === 'string' ? this.startDate.slice(0, 10) : formatYmd(defaultStartDate()));
      const params = ['program_id=' + this.program.id, 'start_date=' + date];
      const res = await ApiService.get('statistic/dashboard-online?' + params.join('&'));
      const data = res && res.data;
      this.stat = (data && Array.isArray(data.courses))
        ? data
        : { all_unique_online: 0, courses: [] };
      this.expanded = {};
      this.loaded = true;
    }
  },
  watch: {
    program(p) { if (p) this.loadStat(); },
    startDate() { this.loadStat(); }
  },
  async mounted() {
    if (!this.$hasRole('ROLE_SUPER_ADMIN')) {
      this.$router.replace('/lk').catch(() => {});
      return;
    }
    const programsRes = await ApiService.get('statistic/dashboard-programs');
    this.programs = programsRes.data || [];
    if (this.programs.length) {
      this.program = this.programs[0];
    }
  }
};
</script>

<style lang="scss">
@import "@/../src/assets/style/variables.scss";

.admin-dashboard {
  .section__lk-content-items {
    margin-top: 3.2rem;
  }

  .section__lk-content-items-title {
    background: #002E5E;
    padding: 3rem 4.8rem;
    color: #fff;
    font-size: 2.4rem;
    font-weight: 500;
    line-height: 2.4rem;
    @media (max-width: 768px) {
      font-size: 18px;
      line-height: 24px;
    }
  }

  .course-card {
    &__head {
      padding: 1.6rem 4.8rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      user-select: none;
    }

    &__title {
      font-size: 1.8rem;
      font-weight: 600;
      color: #002E5E;
    }

    &__chevron {
      display: inline-flex;
      transition: transform 0.2s;
      &.is-open {
        transform: rotate(180deg);
      }
    }

    &__body {
      padding: 0 4.8rem 2.4rem;
    }

    .event-stat {
      margin-top: 0;
    }
  }

  .course-stat {
    margin-top: 1.2rem;
  }

  .modules {
    margin-top: 2.4rem;
    border-top: 2px solid $blue-0;
    padding-top: 1.6rem;
  }

  .module-block {
    margin-top: 1.6rem;
    padding: 1.2rem 0;
    border-bottom: 1px solid #e5e7eb;
    &:last-child { border-bottom: 0; }
    &__title {
      font-size: 1.6rem;
      font-weight: 600;
      color: #002E5E;
      margin-bottom: 0.8rem;
    }
  }

  .module-stat {
    display: flex;
    flex-wrap: wrap;
    gap: 2.4rem;
    &__cell {
      display: flex;
      align-items: flex-start;
      gap: 0.8rem;
      flex: 1 1 22%;
      min-width: 18rem;
      img { width: 2.4rem; margin-top: 0.4rem; }
    }
    &__value {
      font-size: 2.4rem;
      font-weight: 600;
      color: #002E5E;
      line-height: 1;
    }
    &__label {
      font-size: 1.4rem;
      color: #6b7280;
      margin-top: 0.4rem;
    }
  }
}
</style>
